import React, { useRef, useEffect, useState } from 'react';
import ChatMessage from './ChatMessage';
import { DocumentResponse } from '../services/apiService';

interface LocalMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  documents?: DocumentResponse[];
}

interface ChatMessagesProps {
  messages: LocalMessage[];
  isLoading: boolean;
  onCanvasOpen: (content: string) => void;
  citationProcessingEnabled: boolean;
}

export default function ChatMessages({ messages, isLoading, onCanvasOpen, citationProcessingEnabled }: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const lastScrollTop = useRef(0);
  const isScrollingProgrammatically = useRef(false);

  // Scroll to bottom of messages
  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      isScrollingProgrammatically.current = true;
      messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
      // Reset the flag after a short delay
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 100);
    }
  };

  // Check if user is at the bottom of the scroll container
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold
      setShowScrollButton(!isAtBottom && messages.length > 0);
      
      // Update auto-scroll behavior based on user position
      if (isAtBottom) {
        setShouldAutoScroll(true);
        setUserScrolledUp(false);
      }
    }
  };

  // Handle scroll events to detect user scroll behavior
  const handleScroll = () => {
    if (scrollContainerRef.current && !isScrollingProgrammatically.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      
      // Detect if user scrolled up
      if (scrollTop < lastScrollTop.current && !isAtBottom) {
        setUserScrolledUp(true);
        setShouldAutoScroll(false);
      }
      // Detect if user scrolled down to bottom
      else if (scrollTop > lastScrollTop.current && isAtBottom) {
        setUserScrolledUp(false);
        setShouldAutoScroll(true);
      }
      
      lastScrollTop.current = scrollTop;
      checkScrollPosition();
    }
  };

  // Auto-scroll to bottom when new messages arrive, but only if user hasn't scrolled up
  useEffect(() => {
    if (shouldAutoScroll && !userScrolledUp) {
      // Use a small delay to ensure the DOM has updated
      const timeoutId = setTimeout(() => {
        scrollToBottom(false); // Don't use smooth scroll during streaming for better performance
      }, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, shouldAutoScroll, userScrolledUp]);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      // Initial check
      checkScrollPosition();
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [messages.length]);

  // Handle scroll to bottom button click
  const handleScrollToBottomClick = () => {
    setUserScrolledUp(false);
    setShouldAutoScroll(true);
    scrollToBottom(true); // Use smooth scroll for manual clicks
  };

  return (
    <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 lg:p-6 theme-bg-primary relative" ref={scrollContainerRef}>
      <div className="max-w-7xl mx-auto">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            isStreaming={isLoading && messages[messages.length - 1]?.id === message.id}
            onCanvasOpen={onCanvasOpen}
            citationProcessingEnabled={citationProcessingEnabled}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={handleScrollToBottomClick}
          className="fixed bottom-20 lg:bottom-24 right-4 lg:right-6 p-3 theme-primary theme-text-inverse rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-30 hover:scale-105"
          title="Scroll to bottom"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}
    </div>
  );
}
